import { Link } from "react-router-dom";
import {
    ArrowRight,
    Waves,
    CloudRain,
    CloudLightning,
    Layers,
    Building2,
    Camera,
    FileText,
    ListChecks,
    CheckCircle2,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { SingleReviewBlock, REVIEWS } from "../components/ClientReviews";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";

const SITE_URL = "https://strataroofmanagement.com.au";
const PATH = "/sunshine-coast-strata-roof-assessments";
const EYEBROW =
    "font-display font-bold tracking-[0.26em] uppercase text-srm-gold text-[12px] lg:text-[13px]";
const GOLD_RULE = "block h-[3px] w-16 bg-srm-gold";
const H2 =
    "font-display font-bold text-[1.6rem] sm:text-[2rem] lg:text-[2.3rem] tracking-[-0.02em] leading-[1.16] text-srm-navy text-balance";
const BODY = "text-[15px] lg:text-[16px] text-srm-body leading-[1.8]";
const INLINE_LINK =
    "text-srm-navy font-medium underline underline-offset-4 decoration-srm-gold/60 hover:text-srm-gold transition-colors";

const CONDITIONS = [
    {
        icon: Waves,
        title: "Coastal and salt exposure",
        body: "Buildings close to the coast are exposed to salt-laden air. Over time this can accelerate corrosion of metal roof sheeting, fixings, flashings, gutters and rooftop plant supports, particularly where surfaces are not regularly rinsed by rain.",
    },
    {
        icon: CloudRain,
        title: "Heavy rainfall and drainage",
        body: "Intense summer rainfall places real load on gutters, box gutters, outlets and downpipes. Blocked or undersized drainage can allow water to back up beneath roof coverings or into the building during a single heavy event.",
    },
    {
        icon: CloudLightning,
        title: "Severe-weather preparation",
        body: "South-east Queensland\u2019s storm season brings strong winds and heavy rain. Knowing the current condition of fixings, sheeting, tiles and flashings before the season helps committees prioritise maintenance rather than respond after damage occurs.",
    },
    {
        icon: Layers,
        title: "Flashings, membranes and penetrations",
        body: "Many leaks begin at junctions rather than in the main roof surface: flashings, sealants, waterproofing membranes, skylights, vents and the penetrations around air-conditioning and other plant.",
    },
    {
        icon: Building2,
        title: "Apartment and mixed-use buildings",
        body: "Sunshine Coast strata schemes range from low-rise townhouse complexes to multi-storey apartment and mixed-use buildings. Larger roofs often combine metal, tile and membrane areas, rooftop plant and fall-prevention systems, each with different maintenance needs.",
    },
];

const DELIVERABLES = [
    {
        icon: Camera,
        title: "Photographic condition evidence",
        body: "Dated photographs record what was observed and where, giving the committee a reference point for future comparison.",
    },
    {
        icon: FileText,
        title: "Committee-ready reporting",
        body: "Findings are written for strata managers and committees, explaining the condition, the risk and why each item matters.",
    },
    {
        icon: ListChecks,
        title: "Prioritised recommendations",
        body: "Recommendations are based on observed condition and documented evidence, so the committee can plan and budget in order of priority.",
    },
];

const STAGES = [
    { title: "Assessment & Report", body: "An independent roof condition assessment and report." },
    { title: "Scope of Works & Tender", body: "Where works are required, a defined scope and comparable contractor submissions." },
    { title: "Delivery Oversight", body: "Where required, independent review of progress, quality, variations and completion." },
    { title: "Ongoing Guardianship & Monitoring", body: "Where required, a maintained asset record and continuing monitoring of priorities." },
];

const SERVICE_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Independent strata roof assessments \u2014 Sunshine Coast",
    serviceType: "Strata roof condition assessment",
    url: `${SITE_URL}${PATH}`,
    description:
        "Independent roof condition assessments, photographic condition evidence and committee-ready reporting for strata and body corporate buildings on the Sunshine Coast, with optional scope and tender support, delivery oversight and ongoing monitoring.",
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: { "@type": "AdministrativeArea", name: "Sunshine Coast, Queensland" },
};

function LocalHero() {
    const ref = useReveal();
    return (
        <section ref={ref} data-testid="sc-hero" className="bg-white">
            <div className="container-srm pt-12 pb-12 lg:pt-16 lg:pb-16">
                <div className="reveal max-w-[780px] mx-auto text-center">
                    <div className={EYEBROW}>Sunshine Coast, Queensland</div>
                    <span aria-hidden className={`mx-auto mt-4 ${GOLD_RULE}`} />
                    <h1
                        data-testid="sc-hero-headline"
                        className="mt-5 font-display font-bold text-[1.95rem] sm:text-[2.3rem] lg:text-[2.7rem] tracking-[-0.024em] leading-[1.1] text-srm-navy text-balance"
                    >
                        Independent Strata Roof Assessments &mdash; Sunshine Coast
                    </h1>
                    <p data-testid="sc-hero-intro" className={`mt-6 max-w-[660px] mx-auto ${BODY}`}>
                        SRM is based on the Sunshine Coast and provides independent roof
                        condition assessments and committee-ready reports for strata
                        managers, body corporate committees and professionally managed
                        buildings. Our advice is property-focused and separate from
                        repair work, so recommendations reflect the condition of the
                        roof and the evidence observed.
                    </p>
                </div>
            </div>
        </section>
    );
}

function LocalConditions() {
    const ref = useReveal();
    return (
        <section ref={ref} data-testid="sc-conditions" className="bg-[#F7F5F0] border-t border-srm-line">
            <div className="container-srm py-14 sm:py-16 lg:py-20">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>Local Conditions</div>
                    <span aria-hidden className={`mx-auto mt-4 ${GOLD_RULE}`} />
                    <h2 className={`mt-6 ${H2}`}>What coastal conditions mean for strata roofs.</h2>
                    <p className={`mt-5 ${BODY}`}>
                        Every building is different, but Sunshine Coast roofs commonly
                        share a few exposures that an assessment should consider.
                    </p>
                </div>
                <ul className="mt-8 sm:mt-10 lg:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
                    {CONDITIONS.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <li
                                key={c.title}
                                data-testid={`sc-condition-${i}`}
                                className="reveal relative overflow-hidden rounded-xl bg-white ring-1 ring-srm-line p-5 sm:p-6 lg:p-7 shadow-[0_3px_14px_rgba(15,40,86,0.04)]"
                                style={{ transitionDelay: `${i * 50}ms` }}
                            >
                                <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-12 bg-srm-gold" />
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-srm-gold/45 text-srm-gold">
                                    <Icon className="h-[18px] w-[18px]" strokeWidth={1.7} />
                                </span>
                                <h3 className="mt-4 font-display font-bold text-srm-navy text-[1.05rem] lg:text-[1.12rem] leading-snug">
                                    {c.title}
                                </h3>
                                <p className="mt-3 text-[14px] lg:text-[14.5px] text-srm-body leading-[1.7]">
                                    {c.body}
                                </p>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </section>
    );
}

function LocalDeliverables() {
    const ref = useReveal();
    return (
        <section ref={ref} data-testid="sc-assessment" className="bg-white border-t border-srm-line">
            <div className="container-srm py-14 sm:py-16 lg:py-20">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>The Assessment</div>
                    <span aria-hidden className={`mx-auto mt-4 ${GOLD_RULE}`} />
                    <h2 className={`mt-6 ${H2}`}>An independent roof condition assessment your committee can use.</h2>
                    <p className={`mt-5 ${BODY}`}>
                        SRM inspects the roof and related external elements, including
                        coverings, drainage, flashings, membranes, penetrations and safe
                        access, then documents the findings in a clear report.
                    </p>
                </div>
                <ul className="mt-8 sm:mt-10 lg:mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 max-w-5xl mx-auto">
                    {DELIVERABLES.map((d, i) => {
                        const Icon = d.icon;
                        return (
                            <li
                                key={d.title}
                                data-testid={`sc-deliverable-${i}`}
                                className="reveal relative overflow-hidden rounded-xl bg-white ring-1 ring-srm-line p-5 sm:p-6 lg:p-7 shadow-[0_3px_14px_rgba(15,40,86,0.04)]"
                            >
                                <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-12 bg-srm-gold" />
                                <Icon className="h-5 w-5 text-srm-gold" strokeWidth={1.7} />
                                <h3 className="mt-4 font-display font-bold text-srm-navy text-[1.05rem] lg:text-[1.1rem] leading-snug">
                                    {d.title}
                                </h3>
                                <p className="mt-3 text-[14px] lg:text-[14.5px] text-srm-body leading-[1.7]">
                                    {d.body}
                                </p>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </section>
    );
}

function LocalStages() {
    const ref = useReveal();
    return (
        <section ref={ref} data-testid="sc-stages" className="bg-[#F7F5F0] border-t border-srm-line">
            <div className="container-srm py-14 sm:py-16 lg:py-20">
                <div className="max-w-3xl mx-auto reveal">
                    <div className="text-center">
                        <div className={EYEBROW}>Beyond the Report</div>
                        <span aria-hidden className={`mx-auto mt-4 ${GOLD_RULE}`} />
                        <h2 className={`mt-6 ${H2}`}>Support from assessment to ongoing monitoring.</h2>
                    </div>
                    <ol className="mt-8 sm:mt-10 space-y-3">
                        {STAGES.map((s, i) => (
                            <li
                                key={s.title}
                                data-testid={`sc-stage-${i}`}
                                className="flex items-start gap-3.5 rounded-xl bg-white ring-1 ring-srm-line px-5 py-4 sm:px-6"
                            >
                                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-srm-gold" strokeWidth={1.8} />
                                <div className="min-w-0">
                                    <div className="font-display font-bold tracking-[0.18em] uppercase text-srm-navy/50 text-[10px]">
                                        Stage 0{i + 1}
                                    </div>
                                    <h3 className="mt-0.5 font-display font-bold text-srm-navy text-[1rem] lg:text-[1.05rem] leading-snug">
                                        {s.title}
                                    </h3>
                                    <p className="mt-1.5 text-[14px] text-srm-body leading-[1.65]">{s.body}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                    <p data-testid="sc-flexible" className={`mt-8 text-center ${BODY}`}>
                        Clients may engage SRM for one stage, selected stages or the
                        complete{" "}
                        <Link to="/guardian-program" data-testid="sc-link-guardian" className={INLINE_LINK}>
                            Guardian Program
                        </Link>
                        . See how each stage works on our{" "}
                        <Link to="/services" data-testid="sc-link-services" className={INLINE_LINK}>
                            strata roof assessment services
                        </Link>{" "}
                        page.
                    </p>
                </div>
            </div>
        </section>
    );
}

function LocalCTA() {
    const ref = useReveal();
    return (
        <section ref={ref} data-testid="sc-cta" className="bg-white border-t border-srm-line">
            <div className="container-srm py-14 lg:py-20">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <span aria-hidden className={`mx-auto ${GOLD_RULE}`} />
                    <h2 className={`mt-6 ${H2}`}>Discuss a Sunshine Coast property with SRM.</h2>
                    <p className={`mt-5 max-w-2xl mx-auto ${BODY}`}>
                        Tell us about the building and what the committee needs to
                        understand. We will explain the level of support that suits the
                        property.
                    </p>
                    <div className="mt-8 flex justify-center">
                        <Link
                            to="/contact"
                            data-testid="sc-cta-primary"
                            className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[12px] font-semibold tracking-[0.2em] uppercase text-center px-6 py-3 min-h-[54px] sm:px-10 sm:py-0 sm:min-h-0 sm:h-[54px] border border-srm-gold hover:bg-srm-goldDark hover:border-srm-goldDark transition-all duration-300"
                        >
                            Discuss Your Property
                            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function SunshineCoast() {
    usePageMeta({
        title: "Strata Roof Assessments Sunshine Coast | SRM",
        description:
            "Independent strata roof condition assessments on the Sunshine Coast for body corporate committees, strata managers and managed buildings: photographic evidence, committee-ready reports and support through to ongoing monitoring.",
        path: PATH,
        breadcrumbs: [
            { name: "Home", url: "/" },
            { name: "Sunshine Coast Strata Roof Assessments", url: PATH },
        ],
    });

    return (
        <div data-testid="sunshine-coast-page" className="min-h-screen bg-white">
            <Navbar />
            <main id="main">
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(SERVICE_SCHEMA) }}
                />
                <LocalHero />
                <LocalConditions />
                <LocalDeliverables />
                <LocalStages />
                <SingleReviewBlock
                    review={REVIEWS.hooper}
                    heading="Client experience"
                    testid="sc-review"
                />
                <LocalCTA />
            </main>
            <Footer />
        </div>
    );
}
